// /owners/confirmed — landing target of the operator AND free-tier owner
// confirmation emails (old /operators/confirmed links redirect here, hash
// intact).
// (COL-352). Supabase's confirm link delivers the session via URL hash; the
// auth listener picks it up, roles resolve, and this page forwards the fresh
// operator into their console. A fallback CTA covers the
// expired-link/no-session case.
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Loader2 } from "lucide-react";

const SESSION_WAIT_MS = 8000;

const OperatorConfirmed = () => {
  const navigate = useNavigate();
  const { user, rolesReady, hasOperatorRole, isOwnerPortalOnly } = useAuth();
  const [timedOut, setTimedOut] = useState(false);
  const forwardedRef = useRef(false);

  useEffect(() => {
    const timer = setTimeout(() => setTimedOut(true), SESSION_WAIT_MS);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (forwardedRef.current || !user || !rolesReady) return;
    forwardedRef.current = true;
    // Operators land in their console. Free-tier OWNERS confirm through this
    // same URL — it is the default path, since the six-digit code is off by
    // default — and must reach their portal, not the tenant dashboard.
    // Anyone else who opened this by accident goes where their role belongs.
    navigate(
      hasOperatorRole
        ? "/admin?tab=analytics"
        : isOwnerPortalOnly
          ? "/admin/properties"
          : "/dashboard",
      { replace: true }
    );
  }, [user, rolesReady, hasOperatorRole, isOwnerPortalOnly, navigate]);

  return (
    <div className="flex min-h-[70vh] items-center justify-center px-4">
      <Card className="w-full max-w-md text-center">
        <CardHeader>
          <CardTitle>
            {timedOut && !user ? "Link expired?" : "Confirming your account…"}
          </CardTitle>
          <CardDescription>
            {timedOut && !user
              ? "We couldn't pick up a session from this link. It may have expired — sign in with your email and password instead."
              : "One moment — taking you to your admin console."}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex justify-center">
          {timedOut && !user ? (
            <Button asChild>
              <Link to="/">Go to sign in</Link>
            </Button>
          ) : (
            <Loader2
              className="h-6 w-6 animate-spin text-muted-foreground"
              aria-hidden
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default OperatorConfirmed;
